import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { Card } from "@/components/ui/card";
import { apiClient, handleApiError } from "@/lib/api-client";
import { Loader2, AlertCircle } from "lucide-react";
import { Button } from "@/components/ui/button";

export const Route = createFileRoute("/_layout/checkout/success")({
	component: SuccessPage,
	validateSearch: (search: Record<string, unknown>) => {
		return {
			session_id: (search.session_id as string) || "",
		};
	},
});

function SuccessPage() {
	const { session_id } = Route.useSearch();
	const navigate = useNavigate();
	const [error, setError] = useState<string | null>(null);

	useEffect(() => {
		if (!session_id) {
			setError("No session ID provided");
			return;
		}

		// Verify the session and redirect to redeem page
		const verifyAndRedirect = async () => {
			try {
				const result = await apiClient.checkout.verifySession(session_id);

				// Store flag to hide banner
				localStorage.setItem("blackfriday-banner-dismissed", "true");

				if (result.success && result.redemptionCode) {
					// Guest checkout - redirect to redeem page with code
					navigate({ to: "/redeem", search: { code: result.redemptionCode } });
				} else if (result.success && result.entitled) {
					// Authenticated user already entitled - redirect to home
					navigate({ to: "/" });
				} else {
					setError("Payment verification failed. Please contact support.");
				}
			} catch (err) {
				setError("Failed to verify payment. Please contact support.");
				handleApiError(err, "Failed to verify payment");
			}
		};

		verifyAndRedirect();
	}, [session_id, navigate]);

	// Error state
	if (error) {
		return (
			<div className="min-h-screen bg-white dark:bg-gray-950 flex items-center justify-center p-6">
				<Card className="p-12 max-w-md text-center border border-gray-200 dark:border-gray-800">
					<AlertCircle className="w-16 h-16 mx-auto mb-6 text-red-500" />
					<h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">
						Verification Error
					</h2>
					<p className="text-sm text-gray-500 dark:text-gray-400 mb-6">
						{error}
					</p>
					<Button
						onClick={() => navigate({ to: "/" })}
						className="bg-gray-900 hover:bg-gray-800 text-white dark:bg-white dark:text-gray-900 dark:hover:bg-gray-100"
					>
						Go to Home
					</Button>
				</Card>
			</div>
		);
	}

	// Loading/verifying state
	return (
		<div className="min-h-screen bg-white dark:bg-gray-950 flex items-center justify-center">
			<Card className="p-12 max-w-md text-center border border-gray-200 dark:border-gray-800">
				<Loader2 className="w-12 h-12 mx-auto mb-6 text-gray-400 dark:text-gray-600 animate-spin" />
				<h2 className="text-2xl font-semibold mb-2 text-gray-900 dark:text-white">
					Verifying your payment
				</h2>
				<p className="text-sm text-gray-500 dark:text-gray-400">
					Please wait while we confirm your purchase...
				</p>
			</Card>
		</div>
	);
}
